<template>
  <VProgressBar
    class="shipment-status-bar"
    :total="shipmentStatus.total"
    @open="anchor => $emit('open', anchor)"
  >
    <!-- Отгружено без претензии -->
    <VProgressBarSegment :value="shippedWithoutClaim" color="green"/>
    <!-- В очереди на оплату -->
    <VProgressBarSegment :value="shipmentStatus.progressBar.blue" color="blue"/>
    <!-- Собрано -->
    <VProgressBarSegment :value="shipmentStatus.progressBar.purple" color="purple"/>
    <!-- Претензия: часть отгруженного, но рисуется последней — так принято в интерфейсе -->
    <VProgressBarSegment :value="claimed" color="red"/>

    <template #description>
      <p class="summary">
        <span class="total">
          Всего: {{ shipmentStatus.total.formatSmart(unit) }}
        </span>
        <span
          v-if="shipmentStatus.complainedSubstandard.isZero() === false"
          class="error"
          :title="shipmentStatus.complainedSubstandard.formatSmart(unit)"
        >
          Брак: {{ shipmentStatus.complainedSubstandard.formatSmart(unit) }}
        </span>
      </p>
    </template>
  </VProgressBar>
</template>

<style lang="scss" src="./ShipmentStatusBar.scss"/>

<script lang="ts">

import Qty from '@/core/types/Qty';
import VProgressBar from '@/lib/components/VProgressBar/VProgressBar.vue';
import VProgressBarSegment from '@/lib/components/VProgressBar/VProgressBarSegment.vue';
import type OrderPositionShipmentStatus from '@/domains/Orders/OrderPositionShipmentStatus';
import type Unit from '@/domains/Units/Unit';

/**
 * ShipmentStatusBar — прогресс-бар отгрузки позиции заказа.
 *
 * Сегменты: отгружено, в очереди на оплату, собрано, претензия (последней).
 * Под баром — всего и брак (если есть).
 *
 * Претензия (progressBar.red — недостача по приёмке) не отдельная величина,
 * а часть отгруженного: green её уже включает. Поэтому она вычитается из
 * green и рисуется своим сегментом в конце бара — сумма сегментов остаётся равна
 * отгруженному, total не раздвигается.
 *
 * Событие open пробрасывается из VProgressBar: аргумент — DOM-элемент бара,
 * якорь для попапа отгрузок.
 *
 * @example
 * <ShipmentStatusBar
 *   :shipment-status="row.shipmentStatus"
 *   :unit="row.unit"
 *   @open="(anchor) => OrdersService.showPositionShipmentsPopup(row, anchor)"
 * />
 */
export default defineComponent({
  name: 'ShipmentStatusBar',

  components: { VProgressBar, VProgressBarSegment },

  props: {
    shipmentStatus: {
      type:     Object as PropType<OrderPositionShipmentStatus>,
      required: true,
    },

    /** Единица измерения позиции — для подписи «Всего» под баром; null — без единицы */
    unit: {
      type:    Object as PropType<Unit | null>,
      default: null,
    },
  },

  emits: ['open'],

  computed: {
    /**
     * Претензия, не больше отгруженного. Больше быть не должна (это часть
     * отгруженного), но если пришло — обрезаем, чтобы зелёный не ушёл в минус.
     */
    claimed(): Qty {
      const shipped = new Qty(this.shipmentStatus.progressBar.green ?? 0);
      const claim   = new Qty(this.shipmentStatus.progressBar.red ?? 0);

      if (claim.greaterThan(shipped) === true) {
        console.warn('ShipmentStatusBar: претензия больше отгруженного, обрезана', this.shipmentStatus);
        return shipped;
      } else {
        return claim;
      }
    },

    /** Отгружено без претензии */
    shippedWithoutClaim(): Qty {
      return new Qty(this.shipmentStatus.progressBar.green ?? 0).minus(this.claimed);
    },
  },
});
</script>